import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import { useContext, useEffect, useRef, useState, forwardRef, useMemo } from 'react';
import {
    calculateContainerHeight,
    fetchCurrency,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
} from 'utils/helperMethods';
import StackedBarChart from 'components/molecules/StackedBarChart';
import { UserContext } from 'contexts/ConfigContext';
import Loader from 'components/molecules/Loader';
import useProfitPoolbyHierarchyDataInfiniteScroll from './hooks/useProfitPoolbyHierarchyDataInfiniteScroll';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { FilterContextData } from 'contexts/FilterContext';
import { isEmpty } from 'lodash';

const Y_AXIS_WIDTH = 60;
const MIN_WIDTH_PER_GROUP = 100;

const ProfitPoolbyHierarchyChart = forwardRef<HTMLElement, {}>((props, ref) => {
    const chartHeight = calculateContainerHeight(3);
    const [toLeft, setToLeft] = useState(0);
    const [availableWidth, setAvailableWidth] = useState(0);
    const chartRef = useRef<any>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { appConfig } = useContext(UserContext);
    const currency = fetchCurrency(appConfig) || '';
    const color2 = ['#8EDFEE', '#4557D5', '#9BDE91', '#293BB8'];

    const {
        data: stackedBarChartDataInfiniteScroll,
        groupedChartData,
        tableData: profitPoolTableData,
        min: minInfiniteScroll,
        max: maxInfiniteScroll,
        isLoading: isLoadingInfiniteScroll,
        isError: isErrorInfiniteScroll,
        isSuccess: isSuccessInfiniteScroll,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useProfitPoolbyHierarchyDataInfiniteScroll();

    const minBound = useMemo(() => {
        return minInfiniteScroll < 0 ? minInfiniteScroll : 0;
    }, [minInfiniteScroll]);

    const maxBound = useMemo(() => {
        return maxInfiniteScroll > 0 ? maxInfiniteScroll : 0;
    }, [maxInfiniteScroll]);

    const { multiSelectedTiers, profitPoolTierList } = useContext(FilterContextData);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { tiersColorMapPPOverview, kpiLabelMapping, defaultCurrencySymbol } = profitPoolConfig;

    // Measure available width reactively using ResizeObserver
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setAvailableWidth(entry.contentRect.width);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [groupedChartData]);

    // Dynamic per-group width: fills container when few groups, overflows for scrolling when many
    const perGroupWidth = useMemo(() => {
        const numberOfGroups = groupedChartData.length || 1;
        const chartAreaWidth = availableWidth - Y_AXIS_WIDTH;
        return Math.max(chartAreaWidth / numberOfGroups + 50, MIN_WIDTH_PER_GROUP);
    }, [groupedChartData, availableWidth]);

    // Create a mapping from attributeDesc (description) to attribute_ex_id (ID) from chart data
    // and a lookup map for tier percentages: attributeDesc + tier -> percentage
    const { descToIdMap, tierPercentLookup } = useMemo(() => {
        const descToId: Record<string, string> = {};
        const lookup: Record<string, string> = {};

        // Create mapping from description to ID using chart data
        if (stackedBarChartDataInfiniteScroll) {
            stackedBarChartDataInfiniteScroll.forEach((item: any) => {
                if (item.attributeDesc && item.attribute_ex_id) {
                    descToId[item.attributeDesc] = item.attribute_ex_id;
                }
            });
        }

        // Create lookup map using ID (which is what tableData uses)
        if (profitPoolTableData && multiSelectedTiers) {
            profitPoolTableData.forEach((row: any) => {
                multiSelectedTiers.forEach((tier: string) => {
                    const key = `${row.attributeDesc}_${tier}`;
                    const percentKey = `${tier}Percent`;
                    if (row[percentKey]) {
                        lookup[key] = row[percentKey];
                    }
                });
            });
        }

        return { descToIdMap: descToId, tierPercentLookup: lookup };
    }, [profitPoolTableData, multiSelectedTiers, stackedBarChartDataInfiniteScroll]);

    if (isLoadingInfiniteScroll)
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    if (isErrorInfiniteScroll) return <ErrorBox />;

    if (isSuccessInfiniteScroll && isEmpty(stackedBarChartDataInfiniteScroll)) {
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ErrorBox msg={'No Data'} />
            </div>
        );
    }

    return (
        <>
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                {true ? (
                    <>
                        <div className="text-sm">
                            <p>{`Total ${kpiLabelMapping['profit']} ${defaultCurrencySymbol}`}</p>
                        </div>
                        <div className="flex flex-row gap-2 text-xs">
                            {multiSelectedTiers.map((tier) => {
                                return (
                                    <>
                                        <div
                                            className="w-3 h-2 self-center"
                                            style={{ backgroundColor: tiersColorMapPPOverview?.[tier] }}
                                        />
                                        <p>{profitPoolTierList.find((t) => t.tierReference === tier)?.tierName}</p>
                                    </>
                                );
                            })}
                        </div>
                        <div />
                    </>
                ) : null}
            </div>
            <section ref={ref}>
                <div className="flex overflow-x-auto" ref={wrapperRef}>
                    <div className="">
                        <Barchart
                            padding={[0, 0, 15, 0]}
                            barWidth={0}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`bumpChart-main2`}
                            fields={['attributeDesc', 'abs_profit', 'tier']}
                            minBound={minBound}
                            maxBound={scaleMaxValue(30, maxBound)}
                            width={60}
                            colorKey={'tier'}
                            YGrid={false}
                            yAxisConfig={true}
                            yAxisLabelFormatter={(val: any) => {
                                return `${defaultCurrencySymbol} ${formatNumWithKMBAlt(val)}`;
                            }}
                        />
                    </div>
                    <HorizontalInfiniteScroller
                        id={`growth-analysis-sales-infinite-scroll-profit`}
                        hasMore={hasNextPage}
                        scrollThreshold={0.9}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            !isFetchingNextPage && fetchNextPage();
                            // width > 0 && setToLeft(toLeft + width);
                        }}
                    >
                        <div
                            id="growth-analysis-sales-infinite-scroll-profit"
                            ref={chartRef}
                            className="flex overflow-x-auto max-w-full"
                            style={{ minWidth: perGroupWidth * (groupedChartData?.length || 1) }}
                        >
                            {groupedChartData.map((item: any) => {
                                return (
                                    <div key={item[0].attribute_ex_id}>
                                        <StackedBarChart
                                            barWidth={40}
                                            dataset={item || []}
                                            height={chartHeight}
                                            loading={isFetchingNextPage}
                                            showLoader={false}
                                            intervalLabelKey="totalVal"
                                            intervalFormatter={(val: any) => {
                                                return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                                            }}
                                            chartContainer={`pp-chart-${item[0].attribute_ex_id}`}
                                            fields={['attributeDesc', 'abs_profit', 'tier', 'totalVal', 'index']}
                                            minBound={minBound}
                                            maxBound={scaleMaxValue(30, maxBound)}
                                            width={perGroupWidth}
                                            xLabelFontSize={11}
                                            colorKey={'tier'}
                                            groupedChartColor={color2 as [string, string]}
                                            currency={currency}
                                            YGrid={true}
                                            tooltipFormatter={(xAxis: any, val: any, attributeDesc?: any) => {
                                                const tierName = profitPoolTierList.find(
                                                    (tier) => tier.tierReference === xAxis,
                                                )?.tierName;
                                                // Convert description to ID for lookup (tableData uses ID as key)
                                                const attributeId =
                                                    attributeDesc && descToIdMap[attributeDesc]
                                                        ? descToIdMap[attributeDesc]
                                                        : attributeDesc;
                                                const lookupKey = attributeId ? `${attributeId}_${xAxis}` : null;
                                                const tierPercent =
                                                    lookupKey && tierPercentLookup[lookupKey]
                                                        ? tierPercentLookup[lookupKey]
                                                        : null;
                                                return {
                                                    name: tierName,
                                                    value: tierPercent
                                                        ? `${defaultCurrencySymbol}${formatNumWithKMBAlt(
                                                              val,
                                                              2,
                                                              2,
                                                          )} (${tierPercent})`
                                                        : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                                                };
                                            }}
                                            disableTotalLabelYOffset={true}
                                            toolTipFields={`tier*abs_profit*attributeDesc`}
                                        />
                                        <p
                                            className={`text-xs text-center px-1 ${
                                                isFetchingNextPage ? 'opacity-30' : 'opacity-100'
                                            }`}
                                        >
                                            {item[0].attributeDesc}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
            </section>
        </>
    );
});

export default ProfitPoolbyHierarchyChart;
